import React, { useState, useEffect, useRef } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { trackEditProjectChangeImg } from '../../../../analytics';

import eventCustomField from '../../../../assets/event-custom-field.png';
import eventImages from '../../../../assets/event-images.png';
import eventTsAndCs from '../../../../assets/event-ts-and-cs.png';

import { FloatingLabel, FormText } from '../../../../components/form';
import { DraggerSimplified } from '../../../../components/video';

import { URL } from '../../../../constants';
import { Event } from '../../../../types';
import { getUrl, getEventImg, getBase64 } from '../../../../utils';

import CheckInPageUI from '../../../CheckIn/checkInPageUI';
import { getTsAndCsText } from '../../../CheckIn/components/getTsAndCsText';
import InfoImg from '../components/infoImg';
import TabComponent from '../components/tabComponent';

interface Props {
  event: Event;
}

const CheckInTab: React.FC<Props> = (props) => {
  const { event } = props;

  const [bannerUrl, setBannerUrl] = useState(
    getEventImg('banner', event.banner),
  );
  const [banner, setBanner] = useState<UploadFile | null>(null);
  const [logoUrl, setLogoUrl] = useState(getEventImg('logo', event.logo));
  const [logo, setLogo] = useState<UploadFile | null>(null);
  const [data, setData] = useState({
    tsAndCsLink: event.tsAndCsLink ?? '',
    organiserName: event.organiserName ?? '',
    customFieldLabel: event.customFieldLabel ?? '',
  });

  const previewRef = useRef<HTMLDivElement>(null);
  const [previewScreen, setPreviewScreen] = useState(0);
  const [previewWidth, setPreviewWidth] = useState(0);

  useEffect(() => {
    if (previewRef.current) {
      const rect = previewRef.current.getBoundingClientRect();
      setPreviewWidth(rect.width);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onChangeImg = (file: UploadFile, isBanner: boolean) => {
    getBase64(file, (url: string) => {
      if (isBanner) setBannerUrl(url);
      else setLogoUrl(url);
    });

    if (isBanner) setBanner(file);
    else setLogo(file);

    trackEditProjectChangeImg(isBanner ? 'banner' : 'logo');
  };

  const imgBlockClass = classNames('w-full flex flex-col gap-2', {
    'sm:w-1/2': previewScreen === -1,
  });

  const handlePreviewClick = (type: string) => {
    setPreviewScreen((prev) => {
      if (type === 'left') return prev - 1;
      else if (type === 'right') return prev + 1;
      else return prev;
    });
  };

  return (
    <TabComponent
      title="Check-In Page"
      subtitle={
        <>
          Customise the project{' '}
          <Link to={getUrl(URL.CHECK_IN, event.eventId)} target="_blank">
            Check-In page
          </Link>
          , where your attendees can self check-in.
        </>
      }
      event={event}
      data={{ ...data, banner, logo }}
      blocks={[
        {
          title: 'Banner & Logo',
          description:
            'The banner and logo will be shown at the top of the Check-In page.',
          infoHeader: 'Setting up your Banner & Logo',
          infoBody: (
            <React.Fragment>
              <div>
                The banner and logo will be shown at the top of the Check-In
                page.
              </div>

              <InfoImg src={eventImages} alt="Banner & Logo Graphic" />
            </React.Fragment>
          ),
          content: (
            <div
              className={classNames('flex flex-col', {
                'sm:flex-row gap-6 sm:gap-10': previewScreen === -1,
                'gap-4': previewScreen !== -1,
              })}
            >
              <div className={imgBlockClass}>
                <DraggerSimplified
                  type="image"
                  imgUrl={bannerUrl}
                  isDashboard
                  onDrop={(file: UploadFile) => onChangeImg(file, true)}
                />

                <div className="text-sm italic">
                  Pro Tip: for the best experience, make sure your banner fits a
                  16:9 aspect ratio.
                </div>
              </div>

              <div className={imgBlockClass}>
                <DraggerSimplified
                  type="image"
                  imgUrl={logoUrl}
                  isDashboard
                  onDrop={(file: UploadFile) => onChangeImg(file, false)}
                />

                <div className="text-sm italic">
                  Pro Tip: for the best experience, make sure your logo fits a
                  1:1 aspect ratio.
                </div>
              </div>
            </div>
          ),
        },
        {
          title: 'Terms & Conditions',
          description: (
            <>
              Add your T&C to appear on check-in page along with CrowdClip&reg;
              T&C
            </>
          ),
          infoHeader: 'Setting up your Terms & Conditions',
          infoBody: (
            <React.Fragment>
              <div>
                The Terms & Conditions (if existing) will be shown on the
                Check-In page. Attendees cannot proceed without accepting them.
              </div>

              <InfoImg src={eventTsAndCs} alt="T&C Graphic" />

              <div>
                The Organiser name (if provided) will be shown instead of the
                "event organiser's" text. The T&C hyperlink will lead the
                attendee to the provided Link.
              </div>
            </React.Fragment>
          ),
          content: (
            <React.Fragment>
              <FloatingLabel
                label="Link"
                name="tsAndCsLink"
                value={data.tsAndCsLink}
                onChange={handleChange}
              />

              <FloatingLabel
                label="Organiser name"
                name="organiserName"
                value={data.organiserName}
                onChange={handleChange}
              />

              <div className="text-sm">
                <b>What attendees see</b>:{' '}
                {getTsAndCsText(data.tsAndCsLink, data.organiserName)}
              </div>
            </React.Fragment>
          ),
        },
        {
          title: 'Custom Field',
          description: 'Add a custom property to appear on check-in page',
          infoHeader: 'Setting up your Custom Field',
          infoBody: (
            <React.Fragment>
              <div>
                The Custom Field (if existing) will be shown as an additional
                optional field for attendees to fill on the Check-In page.
              </div>

              <InfoImg src={eventCustomField} alt="Custom Field Graphic" />
            </React.Fragment>
          ),
          content: (
            <div>
              <FloatingLabel
                label="Custom field label"
                name="customFieldLabel"
                value={data.customFieldLabel}
                onChange={handleChange}
              />

              <FormText text="E.g. Seat number" />
            </div>
          ),
        },
      ]}
      preview={{
        previewRef: previewRef,
        show: previewScreen !== -1,
        onShow: () => setPreviewScreen(0),
        onHide: () => setPreviewScreen(-1),
        onClickLeft:
          previewScreen !== 0 ? () => handlePreviewClick('left') : undefined,
        onClickRight:
          previewScreen !== 2 ? () => handlePreviewClick('right') : undefined,
        content: (
          <CheckInPageUI
            isPreview
            width={previewWidth}
            step={previewScreen}
            setStep={setPreviewScreen}
            event={{ ...event, ...data }}
            bannerUrl={bannerUrl}
            logoUrl={logoUrl}
          />
        ),
        note: 'You can fill in the details, but not submit the form',
      }}
    />
  );
};

export default CheckInTab;
